import { EnsureQueryDataOptions, QueryClient } from "@tanstack/react-query";
import type {
  ParamsForMethod,
  ReturnForMethod,
  StandardQueryMethodForModel,
  ModelName,
  TellescopeQueryKey,
  SupportedSession,
} from "../../types";
import { buildTellescopeQueryKey } from "../../keys";
import { executeTellescopeApiMethod } from "../execute-tellescope-api-method";

type EnsureTellescopeQueryDataOptions<
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> = Omit<
  EnsureQueryDataOptions<ReturnForMethod<Model, Method>, TError, TData, TellescopeQueryKey<Model, Method>>,
  "queryKey" | "queryFn"
>;

/**
 * Arguments for ensuring query data exists in cache.
 *
 * @template Model - The Tellescope model name (standard query models only)
 * @template Method - The query method for the model (standard query methods only)
 * @template TError - The error type (defaults to Error)
 * @template TData - The transformed data type (defaults to the method's return type)
 */
export interface EnsureTellescopeQueryDataArgs<
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> extends EnsureTellescopeQueryDataOptions<Model, Method, TError, TData> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The request parameters for the query */
  request: ParamsForMethod<Model, Method>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The Tellescope session instance */
  session: SupportedSession;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}

/**
 * Ensures query data exists in the cache, fetching it if necessary.
 * This is useful when you need to guarantee that data exists before proceeding.
 * Returns cached data if fresh, otherwise fetches new data.
 *
 * Note: This function works with standard (non-paginated) queries only.
 * For infinite queries, use `ensureInfiniteQueryData` instead.
 *
 * @param args - The ensure query data arguments
 * @returns A promise that resolves to the query data (or transformed data if select is used)
 *
 * @example
 * ```tsx
 * // Without transformation
 * const user = await ensureTellescopeQueryData({
 *   queryClient,
 *   session,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * }); // Returns User
 *
 * // With transformation
 * const userName = await ensureTellescopeQueryData({
 *   queryClient,
 *   session,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   select: (user) => user.email,
 * }); // Returns string
 * ```
 */
export const ensureTellescopeQueryData = <
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
>({
  model,
  method,
  request,
  queryClient,
  session,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: EnsureTellescopeQueryDataArgs<Model, Method, TError, TData>): Promise<TData> => {
  const queryKey = buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey });
  return queryClient.ensureQueryData<ReturnForMethod<Model, Method>, TError, TData, TellescopeQueryKey<Model, Method>>({
    queryKey,
    queryFn: () =>
      executeTellescopeApiMethod<Model, Method, false>({
        session,
        model,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      }),
    ...tanstackOptions,
  });
};
